import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderRewardsSummaryEN from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_en_US';
import BeautyInsiderRewardsSummaryFR from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_fr_CA';
import {
    fontSizes, lineHeights, fontWeights, space
} from 'style/config';
import { formatValue } from 'utils/BeautyInsider';
import Empty from 'constants/empty';

/**
 * Renders the left content for BeautyInsider Cash card based on available promotions
 * @param {Array} promotions - Array of available promotion objects with { point, value, segmentExclusive }
 * @param {Object|null} displayedPromotion - The promotion to display (for single or 3+ promotions)
 * @param {string} title - Title text for the card
 * @param {boolean} applied - If true, shows the "for n points" text when rendering the displayed promotion
 */
const BeautyInsiderCashContent = ({ promotions = Empty.Array, displayedPromotion, title, applied = false }) => {
    const isFrench = localeUtils.isFrench();

    // Single or 3+ promotions: use displayedPromotion
    if (displayedPromotion) {
        const formattedValue = formatValue(displayedPromotion.value, isFrench);
        const isSinglePromotion = promotions.length === 1;

        return (
            <Box>
                {title && (
                    <Text
                        is='p'
                        css={styles.title}
                    >
                        {title}
                    </Text>
                )}
                {isSinglePromotion || applied ? (
                    <>
                        <Text
                            is='p'
                            css={styles.primaryText}
                        >
                            {getText('biCashValueOff', [formattedValue])}
                        </Text>
                        <Text
                            is='p'
                            css={styles.secondaryText}
                        >
                            {getText('biCashForPoints', [displayedPromotion.point || 0])}
                        </Text>
                    </>
                ) : (
                    <Text
                        is='p'
                        css={styles.primaryText}
                    >
                        {getText('biCashUpToOff', [formattedValue])}
                    </Text>
                )}
            </Box>
        );
    }

    // Two promotions: show title and both promotion details
    if (promotions.length === 2) {
        const promo1 = promotions[0];
        const promo2 = promotions[1];
        const value1 = formatValue(promo1.value, isFrench);
        const value2 = formatValue(promo2.value, isFrench);

        return (
            <Box>
                {title && (
                    <Text
                        is='p'
                        css={styles.title}
                    >
                        {title}
                    </Text>
                )}
                <Text
                    is='p'
                    css={styles.secondaryText}
                >
                    {getText('biCashTwoPromotionsPrefix', [promo1.point || 0])}
                    <Text
                        is='span'
                        css={styles.boldText}
                    >
                        {getText('biCashTwoPromotionsSuffix', [value1])}
                    </Text>
                </Text>
                <Text
                    is='p'
                    css={styles.secondaryText}
                >
                    {getText('biCashTwoPromotionsPrefix', [promo2.point || 0])}
                    <Text
                        is='span'
                        css={styles.boldText}
                    >
                        {getText('biCashTwoPromotionsSuffix', [value2])}
                    </Text>
                </Text>
            </Box>
        );
    }

    // Fallback: no promotions or empty array
    return null;
};

const styles = {
    title: {
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.bold,
        marginBottom: space[1]
    },
    primaryText: {
        fontSize: `${fontSizes.lg}px`,
        fontWeight: fontWeights.bold,
        marginBottom: space[1],
        lineHeight: lineHeights.tight
    },
    secondaryText: {
        fontSize: `${fontSizes.sm}px`,
        lineHeight: lineHeights.tight
    },
    boldText: {
        fontWeight: fontWeights.bold
    }
};

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderRewardsSummaryEN, BeautyInsiderRewardsSummaryFR);

export default wrapFunctionalComponent(BeautyInsiderCashContent, 'BeautyInsiderCashContent');
